import type { AdminSubmission } from '@l2u/ui/lib/types.ts';

// Pure helpers behind the admin "close the conversation" controls in
// CommentModal. A missing field (older API, test fixtures) always means
// "not closed / nothing to offer", never a guess (Review Focus 5).

export type ConversationFields = Pick<
  AdminSubmission,
  'status' | 'live_status' | 'current_round_latest_author' | 'conversation_closed_at' | 'approvals'
>;

/**
 * The close actions apply only to an approved compliance document, judged on
 * the LIVE status: a draft verdict staged for Notify overlays `status`, so a
 * draft Approve must not offer a close and a draft reject must not hide one.
 */
export function canCloseConversation(
  question: { include_in_compliance_documents: boolean },
  sub: ConversationFields | undefined,
): boolean {
  if (!sub) return false;
  return (
    (sub.live_status ?? sub.status) === 'approved' &&
    question.include_in_compliance_documents === true
  );
}

/** The admin-view round (floored at the approval and the close) has a message. */
export function conversationIsOpen(sub: ConversationFields | undefined): boolean {
  return sub?.current_round_latest_author != null;
}

/**
 * The close stamp to show as "Conversation closed {date}", or null. Hidden once
 * an admin comment reopened the round, once a later approval superseded the
 * close (Review Focus 7), and once the LIVE status is no longer approved (a
 * re-review as rejected opens the vendor's reply door). ISO strings compare as
 * text, like the API.
 */
export function closedLineAt(sub: ConversationFields | undefined): string | null {
  const at = sub?.conversation_closed_at;
  if (!sub || !at) return null;
  if ((sub.live_status ?? sub.status) !== 'approved') return null;
  if (sub.current_round_latest_author != null) return null;
  const lastApproval = sub.approvals?.at(-1);
  if (lastApproval !== undefined && !(at > lastApproval)) return null;
  return at;
}

/**
 * The newest `created_at` the admin has seen, sent as the close endpoint's
 * `seen_through`. Run it over the WHOLE thread the popup fetched (the server
 * scans every comment) and return the API's own string: the server compares
 * as text, so a Date round-trip (microseconds lost, "+00:00" → "Z") would
 * make the newest comment look unseen.
 */
export function seenThrough(comments: readonly { created_at: string }[]): string | null {
  let newest: string | null = null;
  for (const c of comments) {
    if (newest === null || c.created_at > newest) newest = c.created_at;
  }
  return newest;
}

// The popup's row lives in the page's submissions list, whose re-fetch takes
// ~2 s, so the close controls lagged every write. These apply what the write
// itself proved to the cached row straight away; the caller still invalidates,
// and the re-fetch then confirms the same values.
export type SubmissionsCache = { submissions: AdminSubmission[] };

function patchRow<T extends SubmissionsCache>(
  data: T | undefined,
  submissionId: string,
  patch: Partial<AdminSubmission>,
): T | undefined {
  if (!data || !data.submissions.some((s) => s.id === submissionId)) return data;
  return {
    ...data,
    submissions: data.submissions.map((s) => (s.id === submissionId ? { ...s, ...patch } : s)),
  };
}

/**
 * After the close endpoint answers: nothing is left in the admin round. The
 * green "Comment added N" count is floored at the close stamp only (not at the
 * approval), so it drops to 0 only when there is a stamp; a null answer (no-op
 * on an empty, never-closed round) proves nothing about it.
 */
export function withConversationClosed<T extends SubmissionsCache>(
  data: T | undefined,
  submissionId: string,
  closedAt: string | null,
): T | undefined {
  return patchRow(data, submissionId, {
    conversation_closed_at: closedAt,
    current_round_latest_author: null,
    ...(closedAt !== null ? { unanswered_admin_comment_count: 0 } : {}),
  });
}

/**
 * After the admin posts a comment: in the admin view a draft counts from its
 * creation (adminVisibleAt), and it is newer than any floor, so the admin is
 * now the round's latest author — which reopens a closed conversation — and
 * the thread's latest commenter. The green count is left for the re-fetch: the
 * server forces it to 0 on some rows, so incrementing it here would flicker.
 */
export function withAdminReply<T extends SubmissionsCache>(
  data: T | undefined,
  submissionId: string,
): T | undefined {
  return patchRow(data, submissionId, {
    current_round_latest_author: 'admin',
    latest_comment_author: 'admin',
  });
}
